import { Injectable } from '@angular/core';
import { Observable, AsyncSubject, Subject } from 'rxjs';

type SubjectType = {
    notification: Notification,
    event: Event
};

@Injectable()
export class PushNotificationsService {
    public isSupported(): boolean {
        return 'Notification' in window;
    }

    public hasPermission(): boolean {
        return this.isSupported() && Notification.permission === 'granted';
    }

    public requestPermission(): Observable<boolean> {
        const subject = new AsyncSubject<boolean>();
        
        if (!this.isSupported()) {
            subject.error("The browser doesn't support notifications");
            subject.complete();
        } else if (this.hasPermission()) {
            subject.next(true);
            subject.complete();
        } else {
            Notification.requestPermission().then(status => {
                subject.next(status === 'granted');
                subject.complete();
            }, error => {
                subject.error(error);
                subject.complete();
            });
        }

        return subject.asObservable();
    }

    public create(title: string, options?: NotificationOptions): Observable<SubjectType> {
        const subject = new Subject<SubjectType>();

        if (!this.isSupported()) {
            subject.error("The browser doesn't support notifications");
            subject.complete();
        } else if (!this.hasPermission()) {
            subject.error("The user hasn't granted permission for notifications");
            subject.complete();
        } else {
            const notification = new Notification(title, options);
            notification.addEventListener('show', (event) => subject.next({ notification, event }));
            notification.addEventListener('click', (event) => subject.next({ notification, event }));
            notification.addEventListener('error', (event) => subject.error({ notification, event }));
            notification.addEventListener('close', () => subject.complete());
        }

        return subject.asObservable();
    }
}
